import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "src/hooks/useApiMutation";
import type {
	ISessionEnroll,
	IUpdateSessionEnrollPayload,
} from "src/Types/module/b2bSession/b2bSessionEnroll.types";

interface Props {
	open: boolean;
	handleClose: () => void;
	enroll: ISessionEnroll;
}

const UpdateSessionEnrollModal: React.FC<Props> = ({
	open,
	handleClose,
	enroll,
}) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const queryClient = useQueryClient();

	const [status, setStatus] = useState<string>(enroll.status || "approved");
	const [invoiceId, setInvoiceId] = useState<string>(
		enroll.invoice_id ? String(enroll.invoice_id) : ""
	);
	const [formError, setFormError] = useState<string | null>(null);

	useEffect(() => {
		if (enroll) {
			setStatus(enroll.status || "approved");
			setInvoiceId(enroll.invoice_id ? String(enroll.invoice_id) : "");
			setFormError(null);
		}
	}, [enroll, open]);

	const { mutate: updateEnroll, isPending } = useApiMutation<
		IUpdateSessionEnrollPayload,
		any
	>({
		method: "PATCH",
		endPoint: `/api/fair/admin/sessions/enroll/${enroll.id}`,
		invalidateQueries: [
			"SessionEnrolls",
			"SessionEnroll",
			"b2b_details",
			"B2B",
		],
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		const payload: IUpdateSessionEnrollPayload = {
			status: status || "approved",
			...(enroll.invoice_id
				? { invoice_id: enroll.invoice_id }
				: invoiceId !== ""
				? { invoice_id: Number(invoiceId) }
				: {}),
		};

		updateEnroll(payload, {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ["b2b_details"] });
				queryClient.invalidateQueries({ queryKey: ["SessionEnrolls"] });
				queryClient.invalidateQueries({ queryKey: ["SessionEnroll"] });
				queryClient.invalidateQueries({ queryKey: ["B2B"] });
				handleClose();
			},
			onError: (err: any) => {
				console.error(err);
				setFormError(err?.message || "Failed to update session enrollment.");
			},
		});
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen={fullScreen}
			maxWidth="xs"
			fullWidth
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					py: 2,
				}}
			>
				<Typography variant="h5" fontWeight={700}>
					Update Session Enrollment
				</Typography>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit}>
				<DialogContent sx={{ p: 3 }}>
					{formError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formError}
						</Alert>
					)}

					<Grid container spacing={2}>
						{/* Status */}
						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel id="edit-enroll-status-label" shrink>
									Status
								</InputLabel>
								<Select
									labelId="edit-enroll-status-label"
									value={status}
									label="Status"
									notched
									onChange={(e) => setStatus(e.target.value)}
								>
									<MenuItem value="approved">Approved</MenuItem>
									<MenuItem value="pending">Pending</MenuItem>
									<MenuItem value="rejected">Rejected</MenuItem>
									<MenuItem value="completed">Completed</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{/* Invoice ID */}
						<Grid item xs={12} hidden>
							<TextField
								fullWidth
								size="small"
								label="Invoice ID"
								type="number"
								placeholder="e.g. 13"
								InputLabelProps={{ shrink: true }}
								value={invoiceId}
								onChange={(e) => setInvoiceId(e.target.value)}
								helperText="Associated invoice ID (optional)"
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						variant="outlined"
						onClick={handleClose}
						disabled={isPending}
					>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={isPending}>
						{isPending ? "Saving..." : "Save Changes"}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default UpdateSessionEnrollModal;
